import { logOut } from '../../helpers/login';
import * as grpcWeb from 'grpc-web';
import { convertErrorMessage } from '../../helpers/rpc';
import { SearchRequest, SearchResponse, AutocompleteRequest, AutocompleteResponse } from '../../rpc/admin/search_pb';
import { AdminServiceClient } from '../../rpc/admin/AdminServiceClientPb';
import { getBaseApiUrl } from '../../helpers/instance';
import { History } from "history";

export const DEFAULT_RESULTS_PER_PAGE = 25;

export class SortField {
    label: string = '';
    field: string = '';
    direction: SearchRequest.Direction = SearchRequest.Direction.DIRECTION_ASCENDING;
}

export interface Autocompleter {
    autocomplete(request: AutocompleteRequest): Promise<AutocompleteResponse>;
}

export interface QueryObserver {
    queryChanged(): void;
}

abstract class Adapter implements Autocompleter {

    private history: History;

    private observers: QueryObserver[] = [];

    constructor(history: History) {
        this.history = history;
    }

    protected abstract performSearch(client: AdminServiceClient, request: SearchRequest): Promise<SearchResponse>;

    protected abstract performAutocomplete(client: AdminServiceClient, request: AutocompleteRequest): Promise<AutocompleteResponse>;

    search(request: SearchRequest): Promise<SearchResponse> {
        return new Promise((resolve, reject) => {
            const client = new AdminServiceClient(getBaseApiUrl(), null, null);
            this.performSearch(client, request).then((response) => {
                resolve(response);
            }).catch((err) => {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    reject(convertErrorMessage(err));
                }
            });
        });
    }

    autocomplete(request: AutocompleteRequest): Promise<AutocompleteResponse> {
        return new Promise((resolve, reject) => {
            const client = new AdminServiceClient(getBaseApiUrl(), null, null);
            this.performAutocomplete(client, request).then((response) => {
                resolve(response);
            }).catch((err) => {
                if (err === 'Forbidden' || err.message === 'Forbidden') {
                    logOut();
                } else {
                    reject(convertErrorMessage(err));
                }
            });
        });
    }

    attach = (observer: QueryObserver) => {
        const index = this.observers.indexOf(observer);
        if (index < 0) {
            this.observers.push(observer);
        }
    }

    detach = (observer: QueryObserver) => {
        const index = this.observers.indexOf(observer);
        if (index >= 0 ){
            this.observers.splice(index, 1);
        }
    }

    setSearchQuery = (q: string) => {
        const original = window.location.search.substring(1);
        const updated = this.setSearchQueryInQuery(original, q);
        this.maybeRoute(original, updated);
    }

    setSearchQueryInQuery = (query: string, q: string): string => {
        const request = this.searchRequestFromQuery(query);
        request.setQuery(q);
        request.setOffset(0);
        request.setFiltersList([]);
        return this.searchRequestToQuery(request);
    }

    setSortOrder = (field: string, direction: SearchRequest.Direction) => {
        const original = window.location.search.substring(1);
        const updated = this.setSortOrderInQuery(original, field, direction);
        this.maybeRoute(original, updated);
    }

    setSortOrderInQuery = (query: string, field: string, direction: SearchRequest.Direction): string => {
        const request = this.searchRequestFromQuery(query);
        request.setSortField(field);
        request.setSortOrder(direction);
        return this.searchRequestToQuery(request);
    }

    private maybeRoute = (originalQuery: string, updatedQuery: string) => {
        if (updatedQuery !== originalQuery) {
            if (updatedQuery) {
                this.history.push(window.location.pathname + '?' + updatedQuery);
            } else {
                this.history.push(window.location.pathname);
            }
            this.observers.forEach((observer) => {
                observer.queryChanged();
            });
        }
    }

    addFilter = (filter: SearchRequest.Filter) => {
        const original = window.location.search.substring(1);
        const updated = this.addFilterToQuery(original, filter);
        this.maybeRoute(original, updated);
    }

    addFilterToQuery = (query: string, filter: SearchRequest.Filter): string => {
        const request = this.searchRequestFromQuery(query);

        let exists = false;
        request.getFiltersList().forEach((existing) => {
            if (filter.getField() === existing.getField()) {
                if (filter.getStringFilter() && existing.getStringFilter()) {
                    if (filter.getStringFilter()!.getValue() === existing.getStringFilter()!.getValue()) {
                        exists = true;
                    }
                } else if (filter.getRangeFilter() && existing.getRangeFilter()) {
                    if (filter.getRangeFilter()!.getTo() === existing.getRangeFilter()!.getTo() &&
                        filter.getRangeFilter()!.getFrom() === existing.getRangeFilter()!.getFrom()) {
                        exists = true;
                    }
                }
            }
        });

        if (!exists) {
            request.setOffset(0);
            request.addFilters(filter);
            return this.searchRequestToQuery(request);
        }

        return query;
    }

    removeFilter = (filter: SearchRequest.Filter) => {
        const original = window.location.search.substring(1);
        const updated = this.removeFilterFromQuery(original, filter);
        this.maybeRoute(original, updated);
    }

    removeFilterFromQuery = (query: string, filter: SearchRequest.Filter): string => {
        const request = this.searchRequestFromQuery(query);
        const updated: SearchRequest.Filter[] = [];

        let changed = false;
        request.getFiltersList().forEach((existing) => {
            let matched = false;
            if (filter.getField() === existing.getField()) {
                if (filter.getStringFilter() && existing.getStringFilter()) {
                    if (filter.getStringFilter()!.getValue() === existing.getStringFilter()!.getValue()) {
                        matched = true;
                    }
                } else if (filter.getRangeFilter() && existing.getRangeFilter()) {
                    if (filter.getRangeFilter()!.getTo() === existing.getRangeFilter()!.getTo() &&
                        filter.getRangeFilter()!.getFrom() === existing.getRangeFilter()!.getFrom()) {
                        matched = true;
                    }
                }
            }

            if (!matched) {
                updated.push(existing);
            } else {
                changed = true;
            }
        });

        if (changed) {
            request.setOffset(0);
            request.setFiltersList(updated);
            return this.searchRequestToQuery(request);
        }

        return query;
    }

    removeAllFilters = () => {
        const original = window.location.search.substring(1);
        const updated = this.removeAllFiltersFromQuery(original);
        this.maybeRoute(original, updated);
    }

    removeAllFiltersFromQuery = (query: string): string => {
        const request = this.searchRequestFromQuery(query);
        request.setFiltersList([]);
        request.setOffset(0);
        return this.searchRequestToQuery(request);
    }

    setLimit = (limit: number) => {
        const original = window.location.search.substring(1);
        const updated = this.setLimitInQuery(original, limit);
        this.maybeRoute(original, updated);
    }

    setLimitInQuery = (query: string, limit: number): string => {
        const request = this.searchRequestFromQuery(query);
        request.setLimit(limit);
        request.setOffset(0);
        return this.searchRequestToQuery(request);
    }

    setOffset = (offset: number) => {
        const original = window.location.search.substring(1);
        const updated = this.setOffsetInQuery(original, offset);
        this.maybeRoute(original, updated);
    }

    setOffsetInQuery = (query: string, offset: number): string => {
        const request = this.searchRequestFromQuery(query);
        request.setOffset(offset);
        return this.searchRequestToQuery(request);
    }

    searchRequestToQuery = (request: SearchRequest): string => {
        let query = '';

        if (request.getQuery()) {
            query = 'q=' + encodeURIComponent(request.getQuery());
        }

        request.getFiltersList().forEach((filter) => {
            if (query) {
                query += '&';
            }
            if (filter.getStringFilter()) {
                query += 'f=' + encodeURIComponent(filter.getField()) + '%3D' + encodeURIComponent(filter.getStringFilter()!.getValue());
            } else if (filter.getRangeFilter()) {
                query += 'nrf=' + encodeURIComponent(filter.getField()) + '%3D' + filter.getRangeFilter()!.getFrom() + '%3A' + filter.getRangeFilter()!.getTo();
            }
        });

        if (request.getOffset()) {
            if (query) {
                query += '&';
            }
            query += 'offset=' + request.getOffset();
        }

        if (request.getLimit() && request.getLimit() !== DEFAULT_RESULTS_PER_PAGE) {
            if (query) {
                query += '&';
            }
            query += 'limit=' + request.getLimit();
        }

        if (request.getSortField()) {
            if (query) {
                query += '&';
            }
            query += 'sort=' + encodeURIComponent(request.getSortField());
        }

        if (request.getSortOrder() === SearchRequest.Direction.DIRECTION_DESCENDING) {
            if (query) {
                query += '&';
            }
            query += 'order=desc';
        }

        return query;
    }

    searchRequestFromQuery = (query: string): SearchRequest => {
        const result = new SearchRequest();
        result.setLimit(DEFAULT_RESULTS_PER_PAGE);
        result.setSortOrder(SearchRequest.Direction.DIRECTION_ASCENDING);
        result.setSortField('');
        result.setOffset(0);
        result.setQuery('');
        result.setFiltersList([]);


        const q = this.getQueryVariable(query, 'q');
        if (q.length) {
            result.setQuery(q[0]);
        }

        const f = this.getQueryVariable(query, 'f');
        f.forEach((value) => {
            const parts = value.split('=');
            if (parts) {
                const name = parts[0];
                if (name) {
                    let value = '';
                    if (parts.length > 1) {
                        value = parts.slice(1).join('=');
                    }
                    const sf = new SearchRequest.Filter.String();
                    sf.setValue(value);
                    const filter = new SearchRequest.Filter();
                    filter.setField(name);
                    filter.setStringFilter(sf);
                    result.addFilters(filter);
                }
            }
        });

        const nrf = this.getQueryVariable(query, 'nrf');
        nrf.forEach((value) => {
            let parts = value.split('=');
            if (parts) {
                const name = parts[0];
                if (name && parts.length === 2) {
                    parts = parts[1].split(':');
                    if (parts.length === 2) {
                        const from = parseFloat(parts[0]);
                        const to = parseFloat(parts[1]);
                        if (!isNaN(from) && !isNaN(to)) {
                            const rf = new SearchRequest.Filter.Range();
                            rf.setFrom(from);
                            rf.setTo(to);
                            const filter = new SearchRequest.Filter();
                            filter.setField(name);
                            filter.setRangeFilter(rf);
                            result.addFilters(filter);
                        }
                    }
                }
            }
        });

        const offsets = this.getQueryVariable(query, 'offset');
        if (offsets && offsets.length) {
            const offset = parseInt(offsets[0]);
            if (!isNaN(offset)) {
                result.setOffset(offset);
            }
        }

        const limits = this.getQueryVariable(query, 'limit');
        if (limits && limits.length) {
            const limit = parseInt(limits[0]);
            if (!isNaN(limit)) {
                result.setLimit(limit);
            }
        }

        const sorts = this.getQueryVariable(query, 'sort');
        if (sorts && sorts.length) {
            result.setSortField(sorts[0]);
        }

        const orders = this.getQueryVariable(query, 'order');
        if (orders && orders.length && orders[0] === 'desc') {
            result.setSortOrder(SearchRequest.Direction.DIRECTION_DESCENDING);
        }

        return result;
    }

    private getQueryVariable = (query: string, name: string): string[] => {
        const result: string[] = [];
        const vars = query.split('&');
        vars.forEach((pair) => {
            const parts = pair.split('=');
            if (parts && this.decodeUrlParameter(parts[0]) === name) {
                if (parts.length > 1) {
                    result.push(this.decodeUrlParameter(parts[1]));
                } else {
                    result.push('');
                }
            }
        });
        return result;
    }

    private decodeUrlParameter = (str: string): string => {
        return decodeURIComponent((str + '').replace(/\+/g, '%20'));
    }

}

export default Adapter;